import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { queryClient } from '@app/utils';
import { APIError, Language, Namespace } from '@data';
import { questionContextTranslationsKeys, questionTranslationsKeys } from '@data/constants';
import {
  useCreateQuestionContextTranslations,
  useCreateQuestionTranslations,
  useEditQuestion,
  useQuestionContextTranslations,
  useQuestionDependencies,
  useQuestionDetails as useQuestionDetailsQuery,
  useQuestionOptions,
  useQuestionTranslations,
} from '@data/hooks';
import { QuestionType, QuestionUpdateBody, VisibilityType } from '@data/sdk/match';
import { AntdForm, antdNotification } from '@ui/components';
import { getInitialValues, transformQuestionFormValues } from '../utils';

export type QuestionFormValues = {
  autoTranslateText: boolean;
  context: {
    [key in Language]: string;
  };
  expiry: number | null;
  questionType: QuestionType;
  text: {
    [key in Language]: string;
  };
  unit: string;
  visibilityType: VisibilityType;
};

const TRANSLATIONS_REFETCH_DELAY_MS = 4000;

export const useQuestionDetails = () => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { questionId = '' } = useParams<{ questionId: string }>();

  const { data: question, isLoading: isQuestionLoading } = useQuestionDetailsQuery(questionId);
  const { data: translations } = useQuestionTranslations(questionId);
  const { data: contextTranslations } = useQuestionContextTranslations(questionId);
  const { data: dependencies } = useQuestionDependencies(questionId);
  const { data: options, isLoading: isOptionsLoading } = useQuestionOptions(questionId, {
    offset: 0,
    limit: 100,
  });

  const [form] = AntdForm.useForm<QuestionFormValues>();
  const [currentLanguage, setCurrentLanguage] = useState<Language>(Language.EN);
  const [contextLanguage, setContextLanguage] = useState<Language>(Language.EN);

  const { mutate: editQuestion } = useEditQuestion();
  const { mutateAsync: createTranslations } = useCreateQuestionTranslations();
  const { mutateAsync: createContextTranslations } = useCreateQuestionContextTranslations();

  const initialValues: QuestionFormValues = getInitialValues(
    question,
    translations?.items,
    contextTranslations?.items,
  );

  const text = AntdForm.useWatch('text', form);
  const context = AntdForm.useWatch('context', form);
  const visibilityType = AntdForm.useWatch('visibilityType', form);
  const questionType = AntdForm.useWatch('questionType', form);
  const unit = AntdForm.useWatch('unit', form);
  const expiry = AntdForm.useWatch('expiry', form);
  const autoTranslateText = AntdForm.useWatch('autoTranslateText', form);

  const currentLanguageQuestionText = AntdForm.useWatch(['text', currentLanguage], form);
  const enQuestionText = form.getFieldValue(['text', Language.EN]);
  const questionText = currentLanguageQuestionText || enQuestionText;
  const isEnglishTextDirty = (text?.[Language.EN] || '') !== initialValues.text[Language.EN];

  const isFormDirty = useMemo(() => {
    const isVisibilityTypeDirty = visibilityType !== initialValues.visibilityType;
    const isTranslationsDirty = Object.keys(text || {}).some(
      (key) => text[key as Language] !== initialValues.text[key as Language],
    );
    const isContextTranslationsDirty = Object.keys(context || {}).some(
      (key) => context[key as Language] !== initialValues.context[key as Language],
    );
    const isQuestionTypeDirty = questionType !== initialValues.questionType;
    const isUnitDirty = unit !== initialValues.unit;
    const isExpiryDirty = expiry !== initialValues.expiry;

    return {
      isQuestionTypeDirty,
      isTranslationsDirty,
      isContextTranslationsDirty,
      isVisibilityTypeDirty,
      isExpiryDirty,
      isDirty:
        isVisibilityTypeDirty ||
        isTranslationsDirty ||
        isContextTranslationsDirty ||
        isQuestionTypeDirty ||
        isUnitDirty ||
        isExpiryDirty,
    };
  }, [initialValues, text, context, visibilityType, unit, expiry]);

  const handleResetForm = () => {
    form.resetFields();
  };

  const onError = (err: APIError<QuestionUpdateBody>) =>
    antdNotification.error({
      title: t(`patient:beErrors.${err.response?.data.message}`, {
        defaultValue: t('patient:beErrors.unexpectedError'),
        item: t('question_page.details.form.question'),
      }),
    });

  const invalidateQueryWithDelay = (queryKey: readonly unknown[]) => {
    queryClient.invalidateQueries({ queryKey });
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey });
    }, TRANSLATIONS_REFETCH_DELAY_MS);
  };

  const handleEditQuestion = async (values: QuestionFormValues) => {
    if (!question) return;

    const { translationsValues, contextValues, questionValues, isContextEmpty } =
      transformQuestionFormValues(values, initialValues, {
        autoTranslateText: !!values.autoTranslateText && isEnglishTextDirty,
      });

    editQuestion(
      { id: question.id, values: questionValues },
      {
        onSuccess: async () => {
          if (isFormDirty.isTranslationsDirty) {
            await createTranslations({
              id: question.id,
              values: { translations: translationsValues },
            });
          }
          if (isFormDirty.isContextTranslationsDirty && !isContextEmpty) {
            await createContextTranslations({
              id: question.id,
              values: { translations: contextValues },
            });
          }
          invalidateQueryWithDelay(questionTranslationsKeys.list(question.id));
          invalidateQueryWithDelay(questionContextTranslationsKeys.list(question.id));
        },
        onError,
      },
    );
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [question, translations?.items, contextTranslations?.items]);

  useEffect(() => {
    form.setFieldValue('autoTranslateText', isEnglishTextDirty);
  }, [isEnglishTextDirty, form]);

  return {
    dependencies,
    translations,
    options,
    isOptionsLoading,
    questionHelpers: {
      form,
      question,
      isQuestionLoading,
      currentLanguage,
      contextLanguage,
      initialValues,
      isEnglishTextDirty,
      autoTranslateText: !!autoTranslateText,
      visibilityType,
      questionText,
      isFormDirty: isFormDirty.isDirty,
      handleEditQuestion,
      handleResetForm,
      setCurrentLanguage,
      setContextLanguage,
    },
  };
};
